import {
  Card,
  CardContent,
  CardHeader,
  Typography,
  InputAdornment,
  IconButton,
  Box
} from '@mui/material';
import { Email, Lock, Visibility, VisibilityOff } from '@mui/icons-material';
import { memo, ReactNode, useState } from "react";
import LabeledInput from "../../../components/common/fields/LabeledInput";

const ACCENT_COLOR = '#ba2156';

const adornment = (position: 'start' | 'end', node: ReactNode) => (
  <InputAdornment position={position}>{node}</InputAdornment>
);

const visibilityToggle = (visible: boolean, onToggle: () => void, label: string) => (
  <IconButton onClick={onToggle} edge="end" aria-label={label}>
    {visible ? <VisibilityOff /> : <Visibility />}
  </IconButton>
);

interface AccountSettingsSectionProps {
  email: string;
  password: string;
  confirmPassword: string;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onConfirmPasswordChange: (value: string) => void;
  isEdit?: boolean;
}

const AccountSettingsSectionComponent = ({
  email,
  password,
  confirmPassword,
  onEmailChange,
  onPasswordChange,
  onConfirmPasswordChange,
  isEdit = false,
}: AccountSettingsSectionProps) => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const passwordsMatch = password === confirmPassword;
  const showPasswordMismatch = confirmPassword.length > 0 && !passwordsMatch;

  return (
    <Card elevation={2}>
      <CardHeader
        avatar={<Lock sx={{ color: '#ba2156' }} />}
        title={
          <Typography variant="h6" component="h3">
            Account Login
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Update your salon's login credentials for secure access
          </Typography>
        }
      />
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <LabeledInput
            label="Email Address"
            type="email"
            placeholder="salon@example.com"
            value={email}
            onChange={(e) => onEmailChange(e.target.value)}
            disabled={isEdit}
            startAdornment={adornment('start', <Email sx={{ color: ACCENT_COLOR }} />)}
          />
          <LabeledInput
            label="Password"
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter new password"
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            startAdornment={adornment('start', <Lock sx={{ color: ACCENT_COLOR }} />)}
            endAdornment={adornment('end', visibilityToggle(showPassword, () => setShowPassword(!showPassword), 'toggle password visibility'))}
          />
          <LabeledInput
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            placeholder="Confirm new password"
            value={confirmPassword}
            onChange={(e) => onConfirmPasswordChange(e.target.value)}
            error={showPasswordMismatch}
            helperText={showPasswordMismatch ? 'Passwords do not match' : ''}
            startAdornment={adornment('start', <Lock sx={{ color: ACCENT_COLOR }} />)}
            endAdornment={adornment('end', visibilityToggle(showConfirmPassword, () => setShowConfirmPassword(!showConfirmPassword), 'toggle confirm password visibility'))}
          />
        </Box>
      </CardContent>
    </Card>
  );
};

export const AccountSettingsSection = memo(AccountSettingsSectionComponent);
